import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Button,
  ImageList,
  ImageListItem,
} from '@mui/material';
import {
  LocationOn,
  CheckCircle,
  Pending,
  Cancel,
  CameraAlt,
  BrokenImage,
  OpenInNew,
} from '@mui/icons-material';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import SatelliteViewDialog from '../components/SatelliteViewDialog';
import DeepLawnMeasurementsDialog from '../components/DeepLawnMeasurementsDialog';
import PaymentBreakdownTable from '../components/PaymentBreakdownTable';
import { DEFAULT_PLATFORM_FEES } from '../utils/feeCalculation';
import { buildCustomerPaymentBreakdownLines } from '../utils/paymentBreakdownLines';

// Photo component with loading and error handling
const PhotoItem: React.FC<{ url: string; alt: string }> = ({ url, alt }) => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    setLoading(true);
    setError(false);
  }, [url]);

  return (
    <ImageListItem
      sx={{
        position: 'relative',
        aspectRatio: '1',
        overflow: 'hidden',
        borderRadius: 1,
        bgcolor: 'grey.200',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {loading && !error && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.200',
            zIndex: 1,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}
      {error && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.200',
            color: 'text.secondary',
            p: 1,
            zIndex: 1,
          }}
        >
          <BrokenImage sx={{ fontSize: 32, mb: 0.5 }} />
          <Typography variant="caption" align="center" sx={{ mb: 0.5 }}>
            Failed to load
          </Typography>
          <Button
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="outlined"
            startIcon={<OpenInNew />}
            sx={{ textTransform: 'none' }}
          >
            Open in new tab
          </Button>
        </Box>
      )}
      <img
        src={url}
        alt={alt}
        onLoad={() => setLoading(false)}
        onError={() => {
          setLoading(false);
          setError(true);
        }}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          // Keep in layout while loading — display:none + lazy prevented load forever
          opacity: loading || error ? 0 : 1,
          display: 'block',
        }}
      />
    </ImageListItem>
  );
};

const JobDetailsPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const historyCompletedDateParam = searchParams.get('completedDate');
  const { user } = useAuth();
  const [bidDetails, setBidDetails] = useState<any>(null);
  const [photos, setPhotos] = useState<{ before: string[]; after: string[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [satelliteDialog, setSatelliteDialog] = useState({ open: false, address: '' });
  const [measurementsDialogOpen, setMeasurementsDialogOpen] = useState(false);
  const [historyCompletedDate, setHistoryCompletedDate] = useState<string | null>(historyCompletedDateParam);

  useEffect(() => {
    setHistoryCompletedDate(historyCompletedDateParam);
    fetchBidDetails();
  }, [jobId, historyCompletedDateParam]);

  const fetchBidDetails = async () => {
    try {
      setLoading(true);
      setError('');
      setPhotos(null);

      try {
        const serviceRequestResponse = await apiService.get(`/service-requests/${jobId}`);
        if (serviceRequestResponse.success && serviceRequestResponse.data) {
          const responseData = serviceRequestResponse.data as any;
          const serviceRequest = (responseData as any)?.serviceRequest || responseData;

          if (!serviceRequest) {
            throw new Error('Service request data not found in response');
          }

          // If opened from Service History without a completion date param, fall back to Service History API
          if (!historyCompletedDateParam && serviceRequest._id) {
            try {
              const historyId = searchParams.get('historyId');
              const qs = historyId ? `?historyId=${encodeURIComponent(historyId)}` : '';
              const historyRes = await apiService.get(
                `/service-history/by-service-request/${serviceRequest._id}${qs}`
              );
              if (historyRes.success && (historyRes.data as any)?.completedDate) {
                setHistoryCompletedDate(String((historyRes.data as any).completedDate));
              }
            } catch {
              // optional fallback — ignore if endpoint/entry unavailable
            }
          }

          const photosFromApi = responseData.photos ?? { before: [] as string[], after: [] as string[] };
          const hasBefore = Array.isArray(photosFromApi.before) && photosFromApi.before.length > 0;
          const hasAfter = Array.isArray(photosFromApi.after) && photosFromApi.after.length > 0;
          setPhotos({
            before: hasBefore ? photosFromApi.before : [],
            after: hasAfter ? photosFromApi.after : [],
          });

          if (serviceRequest.acceptedBidId) {
            const bidId = typeof serviceRequest.acceptedBidId === 'object'
              ? serviceRequest.acceptedBidId._id || serviceRequest.acceptedBidId
              : serviceRequest.acceptedBidId;

            try {
              const bidResponse = await apiService.get(`/bids/${bidId}`);
              if (bidResponse.success && bidResponse.data) {
                const bidData = bidResponse.data as any;
                // Always prefer the full service request (includes completedDate) over the
                // lightly-populated bid.requestId — that was dropping completion dates in Service Details.
                const existingRequest =
                  bidData.requestId && typeof bidData.requestId === 'object' ? bidData.requestId : {};
                bidData.requestId = { ...existingRequest, ...serviceRequest };
                setBidDetails({ ...bidData, isServiceRequestView: true });
                return;
              }
            } catch (bidErr) {
              console.log('[JOB-DETAILS] Bid not found, showing service request only');
            }
          }

          setBidDetails({
            _id: serviceRequest._id,
            requestId: serviceRequest,
            status: serviceRequest.status || 'completed',
            createdAt: serviceRequest.createdAt || serviceRequest.scheduledDate,
            servicePricing: serviceRequest.acceptedBidId?.servicePricing || [],
            price: serviceRequest.acceptedBidId?.price || 0,
            isServiceRequestView: true,
          });
          return;
        }
      } catch (serviceRequestErr: any) {
        console.log('[JOB-DETAILS] Service request not found, trying as bid:', serviceRequestErr.message);
      }

      const response = await apiService.get(`/bids/${jobId}`);
      if (response.success && response.data) {
        const bidData = response.data as any;
        // Providers viewing a bid (My Bids / deep link) use the canonical Reviews & Bids dialog
        if (user?.userType?.toLowerCase() === 'provider' && !historyCompletedDateParam) {
          const bidId = String(bidData.id || bidData._id || jobId);
          setLoading(false);
          navigate(`/provider/performance?bidId=${encodeURIComponent(bidId)}`, { replace: true });
          return;
        }
        setBidDetails(bidData);
      } else {
        setError('Job details not found. This may be a service request without an associated bid.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'bidding':
        return 'info';
      case 'accepted':
        return 'success';
      case 'scheduled':
        return 'primary';
      case 'in-progress':
        return 'secondary';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatServiceTypes = (serviceType: any): string => {
    if (!serviceType) return 'N/A';
    
    // If it's a string with comma-separated values
    if (typeof serviceType === 'string') {
      return serviceType.split(',').map(service => {
        const trimmed = service.trim();
        // Format specific service names
        switch (trimmed.toLowerCase()) {
          case 'mowing':
            return 'Lawn Mowing';
          case 'edging':
          case 'edgingtrimming':
            return 'Trimming';
          case 'fertilizing':
          case 'fertilizingweedcontrol':
            return 'Fertilizing/Weed Control';
          default:
            // Capitalize first letter of each word
            return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
        }
      }).join(', ');
    }
    
    // If it has a name property
    if (serviceType.name) {
      return formatServiceTypes(serviceType.name);
    }
    
    // If it's an array
    if (Array.isArray(serviceType)) {
      return serviceType.map(service => formatServiceTypes(service)).join(', ');
    }
    
    return serviceType.toString();
  };

  const formatPropertyType = (propertyType: string) => {
    if (!propertyType) return 'N/A';
    
    const typeMap: { [key: string]: string } = {
      'single_residential': 'Single Family Residential',
      'multi_family_condo': 'Multi-Family / Condo',
      'commercial': 'Commercial',
      'vacant_lot': 'Vacant Lot',
      'hoa_managed': 'HOA-Managed Property',
    };
    
    return typeMap[propertyType] || propertyType;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !bidDetails) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          {error || 'Job details not found'}
        </Alert>
      </Container>
    );
  }

  const serviceRequest = bidDetails.requestId || bidDetails;
  const bid = bidDetails.isServiceRequestView ? null : bidDetails;

  const resolveServiceCompletionDate = (): Date | null => {
    // Prefer the Service History occurrence date when opened as an invoice from history
    if (historyCompletedDate) {
      const d = new Date(historyCompletedDate);
      if (!Number.isNaN(d.getTime())) return d;
    }
    const srDate = serviceRequest?.completedDate;
    if (srDate) {
      const d = new Date(srDate);
      if (!Number.isNaN(d.getTime())) return d;
    }
    return null;
  };

  // Simplified Service Details (provider reference) — only existing API data. No backend changes.
  if (bidDetails.isServiceRequestView) {
    const cust = serviceRequest?.customerId as { firstName?: string; lastName?: string; email?: string; phone?: string } | undefined;
    const addr = serviceRequest?.address as { street?: string; city?: string; state?: string; zipCode?: string } | undefined;
    const hasPhotos = photos && (photos.before.length > 0 || photos.after.length > 0);
    const completionDate = resolveServiceCompletionDate();

    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Service Details
        </Typography>

        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Customer */}
            {cust && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Customer</Typography>
                <Typography variant="body1">
                  {cust.firstName} {cust.lastName}
                  {cust.phone && ` · ${cust.phone}`}
                  {cust.email && ` · ${cust.email}`}
                </Typography>
              </Box>
            )}

            {/* Property address */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Property address</Typography>
              <Typography variant="body1">
                {addr ? [addr.street, addr.city, addr.state, addr.zipCode].filter(Boolean).join(', ') || '—' : '—'}
              </Typography>
            </Box>

            {/* Service Completion Date */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Service Completion Date</Typography>
              <Typography variant="body1">
                {completionDate ? formatDate(completionDate) : '—'}
              </Typography>
            </Box>

            {/* Job ID */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Job ID</Typography>
              <Typography variant="body1" fontFamily="monospace">{jobId || '—'}</Typography>
            </Box>

            {/* Photos — always show section so providers see where to view them */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Photos</Typography>
              {hasPhotos ? (
                <>
                  {photos!.before.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Before ({photos!.before.length})</Typography>
                      <ImageList cols={3} gap={8}>
                        {photos!.before.map((url: string, i: number) => (
                          <PhotoItem key={`b-${i}`} url={url} alt={`Before ${i + 1}`} />
                        ))}
                      </ImageList>
                    </Box>
                  )}
                  {photos!.after.length > 0 && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>After ({photos!.after.length})</Typography>
                      <ImageList cols={3} gap={8}>
                        {photos!.after.map((url: string, i: number) => (
                          <PhotoItem key={`a-${i}`} url={url} alt={`After ${i + 1}`} />
                        ))}
                      </ImageList>
                    </Box>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">No photos for this job.</Typography>
              )}
            </Box>
          </Box>
        </Paper>
      </Container>
    );
  }

  // Bid Details (unchanged)
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Bid Details
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={bid?.status || 'Unknown'}
            color={getStatusColor(bid?.status || '') as any}
            size="small"
          />
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Original Service Request
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Service Type</Typography>
                <Typography variant="body1">{formatServiceTypes(serviceRequest?.serviceType)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Requested Date</Typography>
                <Typography variant="body1">
                  {serviceRequest?.requestedDate ? formatDate(new Date(serviceRequest.requestedDate)) : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Property Type</Typography>
                <Typography variant="body1">{serviceRequest?.propertyType ? formatPropertyType(serviceRequest.propertyType) : 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Customer Instructions</Typography>
                <Typography variant="body1">
                  {serviceRequest?.description && serviceRequest.description !== 'No special instructions' && !serviceRequest.description.startsWith('Services requested:')
                    ? serviceRequest.description
                    : 'No special instructions provided'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {bid && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Your Bid</Typography>
              {bid.servicePricing && bid.servicePricing.length > 0 ? (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>Service Breakdown:</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {bid.servicePricing.map((sp: any, index: number) => (
                      <Box key={index} sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1, border: 1, borderColor: 'grey.200' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight="medium">{sp.serviceName}</Typography>
                          <Typography variant="h6" color="primary" fontWeight="bold">{formatCurrency(sp.price)}</Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Frequency:</strong> {sp.frequency} | <strong>Duration:</strong> {sp.duration}h
                        </Typography>
                        {sp.isSeasonalContract && (
                          <Box sx={{ mt: 1 }}>
                            <Chip label="Seasonal Contract" size="small" color="primary" variant="outlined" />
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Bid Amount</Typography>
                    <Typography variant="h5" color="primary">{formatCurrency(bid.price)}</Typography>
                  </Grid>
                </Grid>
              )}

              <Box sx={{ mt: 2, mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Customer charge breakdown
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Same totals the customer sees when reviewing this bid (service price, sales tax, payment processing, total).
                </Typography>
                <PaymentBreakdownTable
                  lines={buildCustomerPaymentBreakdownLines({
                    servicePricing: bid.servicePricing,
                    fallbackPrice: bid.price,
                    platformFeePercent:
                      bid.platformFeePercentage ?? DEFAULT_PLATFORM_FEES.standardFeePercentage,
                    salesTaxRates: (user as any)?.salesTaxRates,
                    address: serviceRequest?.address,
                    propertyType: serviceRequest?.propertyType,
                  })}
                  footerNote="This matches the customer checkout breakdown. Sales tax (if any) is remitted by you. Payment processing is charged to the customer and is not deducted from your service price."
                />
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip label={bid.status} color={getStatusColor(bid.status) as any} size="small" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Submitted</Typography>
                  <Typography variant="body1">{formatDate(new Date(bid.createdAt))}</Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Service Location</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LocationOn sx={{ color: 'primary.main' }} />
              <Typography variant="body1">
                {serviceRequest?.address
                  ? `${serviceRequest.address.street}, ${serviceRequest.address.city}, ${serviceRequest.address.state} ${serviceRequest.address.zipCode}`
                  : 'Address not available'}
              </Typography>
            </Box>
            {serviceRequest?.address && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Property Assessment</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      const address = `${serviceRequest.address.street}, ${serviceRequest.address.city}, ${serviceRequest.address.state} ${serviceRequest.address.zipCode}`;
                      setSatelliteDialog({ open: true, address });
                    }}
                  >
                    🛰️ Satellite View
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const address = `${serviceRequest.address.street}, ${serviceRequest.address.city}, ${serviceRequest.address.state} ${serviceRequest.address.zipCode}`;
                      window.open(`https://www.google.com/maps?q=${encodeURIComponent(address)}`, '_blank');
                    }}
                  >
                    🗺️ Street View
                  </Button>
                  {serviceRequest?.propertyType && ['single_residential', 'vacant_lot'].includes(serviceRequest.propertyType) && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setMeasurementsDialogOpen(true)}
                    >
                      📐 Measurements
                    </Button>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">View the property layout and assess the work area</Typography>
              </Box>
            )}
          </Paper>

          {photos && (photos.before.length > 0 || photos.after.length > 0) && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CameraAlt sx={{ color: 'primary.main' }} />
                <Typography variant="h6">Service Photos</Typography>
              </Box>
              {photos.before.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>Before Photos ({photos.before.length})</Typography>
                  <ImageList cols={3} gap={8}>
                    {photos.before.map((url: string, index: number) => (
                      <PhotoItem key={`before-${index}`} url={url} alt={`Before ${index + 1}`} />
                    ))}
                  </ImageList>
                </Box>
              )}
              {photos.after.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>After Photos ({photos.after.length})</Typography>
                  <ImageList cols={3} gap={8}>
                    {photos.after.map((url: string, index: number) => (
                      <PhotoItem key={`after-${index}`} url={url} alt={`After ${index + 1}`} />
                    ))}
                  </ImageList>
                </Box>
              )}
            </Paper>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Bid Status</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                {bid?.status === 'accepted' || bid?.status === 'completed' ? <CheckCircle sx={{ color: 'success.main' }} /> :
                 bid?.status === 'rejected' || bid?.status === 'cancelled' ? <Cancel sx={{ color: 'error.main' }} /> :
                 <Pending sx={{ color: 'warning.main' }} />}
                <Typography variant="body1">{bid?.status ? `${bid.status.charAt(0).toUpperCase()}${bid.status.slice(1)}` : 'Unknown'}</Typography>
              </Box>
              {bid && (
                <Typography variant="body2" color="text.secondary">
                  {bid.status === 'pending' && 'Waiting for customer response'}
                  {bid.status === 'accepted' && 'Congratulations! Your bid was accepted'}
                  {bid.status === 'rejected' && 'This bid was not selected'}
                </Typography>
              )}
            </CardContent>
          </Card>

          {serviceRequest?.customerId && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Customer</Typography>
                <Typography variant="body1">
                  {serviceRequest.customerId.firstName} {serviceRequest.customerId.lastName}
                </Typography>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Timeline</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2"><strong>Request Created:</strong> {serviceRequest?.createdAt ? formatDate(new Date(serviceRequest.createdAt)) : 'N/A'}</Typography>
                {bid && (
                  <>
                    <Typography variant="body2"><strong>Bid Submitted:</strong> {formatDate(new Date(bid.createdAt))}</Typography>
                    {bid.status === 'accepted' && <Typography variant="body2" color="success.main"><strong>Bid Accepted!</strong></Typography>}
                  </>
                )}
                {serviceRequest?.scheduledDate && (
                  <Typography variant="body2"><strong>Scheduled:</strong> {formatDate(new Date(serviceRequest.scheduledDate))}</Typography>
                )}
                {resolveServiceCompletionDate() && (
                  <Typography variant="body2" color="success.main"><strong>Completed:</strong> {formatDate(resolveServiceCompletionDate()!)}</Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <SatelliteViewDialog
        open={satelliteDialog.open}
        onClose={() => setSatelliteDialog({ open: false, address: '' })}
        address={satelliteDialog.address}
        title="Property Location View"
      />

      {jobId && serviceRequest?.propertyType && (
        <DeepLawnMeasurementsDialog
          open={measurementsDialogOpen}
          onClose={() => setMeasurementsDialogOpen(false)}
          serviceRequestId={jobId}
          propertyType={serviceRequest.propertyType}
          address={serviceRequest.address ? `${serviceRequest.address.street}, ${serviceRequest.address.city}, ${serviceRequest.address.state} ${serviceRequest.address.zipCode}` : ''}
          initialData={serviceRequest.deepLawnData ?? null}
          onDataFetched={(data) => {
            if (serviceRequest) (serviceRequest as any).deepLawnData = data;
          }}
        />
      )}
    </Container>
  );
};

export default JobDetailsPage;

